import { AccountBalance, Sync, Visibility } from "@mui/icons-material";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { validateChildrenIbans } from "apis/controllers/externalData";
import CustomSimpleAccordion from "qubCommon/Accordions/CustomSimpleAccordion";
import AfmInput from "qubCommon/Inputs/AfmInput";
import IbanInput from "qubCommon/Inputs/IbanInput";
import ReviewSection from "qubCommon/ReviewSection";
import { useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import { createMarkup } from "util/createMarkup";
import { isSuccessfulStudentCategory } from "../studentApplication";

const ChildrenIbans = ({ canEdit = true, settings = {}, review }) => {
  const form = useFormContext();
  const { getValues, setValue } = form;
  const childrenHelp = form.watch("childrenHelp") || [];
  const [checkInfo, setCheckInfo] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const { id } = useParams();

  const handleCheck = async () => {
    setLoading(true);
    try {
      const result = await validateChildrenIbans(id);
      setCheckInfo(result?.data);
      setOpenModal(true);
    } catch (err) {
      console.error("Error during check:", err);
    } finally {
      setLoading(false);
    }
  };

  const beneficiaryChildren = useMemo(
    () =>
      (childrenHelp || []).filter((child) =>
        isSuccessfulStudentCategory(child?.category)
      ),
    [childrenHelp]
  );

  // Initialize / reconcile childrenIbans when children change.
  useEffect(() => {
    if (!beneficiaryChildren.length) return;

    const current = getValues("childrenIbans") || [];

    // Index current by childId for easy merge
    const byId = current.reduce((acc, row) => {
      if (row?.childId) acc[row.childId] = row;
      return acc;
    }, {});

    const merged = beneficiaryChildren.map((child) => {
      const existing = byId[child.id] || {};
      return {
        childId: child.id,
        // keep previously entered values if present
        iban: existing.iban ?? child?.iban ?? "",
        afm: existing.afm ?? child?.afm ?? "", // <- prefill AFM from childrenHelp if available
      };
    });

    setValue("childrenIbans", merged, {
      shouldDirty: false,
      shouldValidate: false,
    });
  }, [beneficiaryChildren, getValues, setValue]);

  useEffect(() => {
    if (review?.wsResponse?.message)
      setCheckInfo({
        message: review?.wsResponse?.message,
        timestamp: review?.wsResponse?.timestamp,
        clipboardText: review?.wsResponse?.clipboardText,
      });
  }, [review]);

  const AccordionTitle = () => (
    <Stack direction="row" alignItems="center" gap={1}>
      <AccountBalance color="primary" fontSize="small" sx={{ ml: "2px" }} />
      <Typography color="primary" sx={{ fontWeight: 1000 }}>
        IBAN ΔΙΚΑΙΟΥΧΩΝ
      </Typography>
    </Stack>
  );

  const renderModalContent = () => {
    if (!checkInfo) return null;

    return <div dangerouslySetInnerHTML={createMarkup(checkInfo?.message)} />;
  };

  const AccordionContent = () => (
    <Grid
      container
      spacing={0}
      sx={{
        border: "1px solid #ddd",
        borderRadius: "8px",
        overflow: "hidden",
        mt: 1,
      }}
    >
      {/* ===== Header Row (desktop only) ===== */}
      <Grid
        container
        item
        xs={12}
        sx={{
          backgroundColor: "#f5f5f5",
          borderBottom: "1px solid #ddd",
          py: 1,
          px: 2,
          display: { xs: "none", md: "flex" }, // hide on mobile
        }}
      >
        <Grid item md={3}>
          <Typography sx={{ fontWeight: 700 }}>ΟΝΟΜΑΤΕΠΩΝΥΜΟ</Typography>
        </Grid>
        <Grid item md={4}>
          <Typography sx={{ fontWeight: 700 }}>ΙΒΑΝ</Typography>
        </Grid>
        <Grid item md={4}>
          <Typography sx={{ fontWeight: 700 }}>ΑΦΜ</Typography>
        </Grid>
      </Grid>

      {/* ===== Data Rows ===== */}
      {beneficiaryChildren.map((child, index) => (
        <Grid
          container
          item
          xs={12}
          alignItems="center"
          sx={{
            borderBottom:
              index === beneficiaryChildren.length - 1
                ? "none"
                : "1px solid #eee",
            py: 2,
            px: 2,
          }}
          key={child.id}
          spacing={2}
        >
          {/* Name column */}
          <Grid item xs={12} md={3}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                whiteSpace: { xs: "normal", md: "nowrap" },
                mb: { xs: 1, md: 0 },
                mt: { md: 2, xs: 0 },
              }}
            >
              {child.firstName} {child.lastName}
            </Typography>
          </Grid>

          {/* IBAN column */}
          <Grid item xs={12} md={4}>
            {/* Label visible only on mobile */}
            <Typography
              variant="subtitle2"
              sx={{
                display: { xs: "block", md: "none" },
                fontWeight: 600,
                mb: 0.5,
              }}
            >
              ΙΒΑΝ
            </Typography>
            <IbanInput
              name={`childrenIbans.${index}.iban`}
              placeholder="π.χ. GR0601101250000000012300695"
              defaultFromProps={false}
              disabled={!canEdit}
            />
          </Grid>

          {/* AFM column */}
          <Grid item xs={12} md={4}>
            {/* Label visible only on mobile */}
            <Typography
              variant="subtitle2"
              sx={{
                display: { xs: "block", md: "none" },
                fontWeight: 600,
                mb: 0.5,
              }}
            >
              ΑΦΜ
            </Typography>
            <AfmInput
              name={`childrenIbans.${index}.afm`}
              placeholder="π.χ. 123456789"
              label={null}
              disabled={!canEdit}
            />
          </Grid>
        </Grid>
      ))}
      <Grid container item xs={12} sx={{ padding: 2 }}>
        <ReviewSection
          canApprove={settings?.canApprove}
          canViewReview={settings?.canViewReview}
          canViewWSResult={settings?.canViewWSResult}
          fieldPrefix="reviews.childrenIbans"
          extra={
            beneficiaryChildren?.length > 0 ? (
              <Grid item xs={12} style={{ display: "flex" }} gap={1}>
                {settings?.canApprove && (
                  <Button
                    onClick={handleCheck}
                    variant="contained"
                    startIcon={
                      loading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        <Sync />
                      )
                    }
                    disabled={loading}
                  >
                    WebService - Έλεγχος
                  </Button>
                )}

                {checkInfo &&
                  (settings?.canViewWSResult || settings?.canApprove) && (
                    <div style={{ marginLeft: "auto", display: "flex" }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        mt={1}
                        mr={1}
                      >
                        Τελευταίος Έλεγχος: {checkInfo.timestamp}
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<Visibility />}
                        onClick={() => setOpenModal(true)}
                      >
                        Αποτελέσματα Ελέγχου
                      </Button>
                    </div>
                  )}
              </Grid>
            ) : null
          }
        />
      </Grid>

      {/* Modal */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Αποτελέσματα Ελέγχου ΙΒΑΝ ΤΕΚΝΩΝ</DialogTitle>
        <DialogContent>{renderModalContent()}</DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="error"
            onClick={() => setOpenModal(false)}
          >
            Κλείσιμο
          </Button>
          {settings?.canApprove && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                form.setValue(
                  "reviews.childrenIbans.comments",
                  checkInfo?.clipboardText
                );
                setOpenModal(false);
              }}
            >
              Ενημέρωση Παρατηρήσεων
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Grid>
  );

  if (!beneficiaryChildren.length) return null;

  return (
    <CustomSimpleAccordion
      title={<AccordionTitle />}
      content={<AccordionContent />}
    />
  );
};

export default ChildrenIbans;
